<template>
    <div>
        <span
            v-for="(snowflake, i) in snowflakes"
            :key="i"
            class="position-fixed"
            :style="snowflake.style"
        />
    </div>
</template>

<script>
import random from "lodash/random"
import isToday from "date-fns/isToday"
import getYear from "date-fns/getYear"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

const MAX_SPEED_OFFSET = 2
const MAX_WIGGLE_OFFSET = 10
const MIN_SIZE = 2
const MAX_SIZE = 10
const MIN_WIGGLE = 10
const MAX_WIGGLE = 40
const MIN_OPACITY = 0.3
const SPEED_CONTROL = 1

// Create the sin table to save processing power later
const sinTable = []
for (let i = 0; i < 100; i++) {
    const sin = Math.sin((i / 100) * Math.PI * 2)
    sinTable.push(sin)
}

export default {
    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        const { primaryColor } = this.$store.state.theme.customization
        return {
            snowflakes: [],
            snowColors: ["#ffffff", primaryColor],
            addNewFlakesEveryNFrames: 10,
            newFlakesToAdd: 3,
            additionCounter: 0,
            fps: 30,
            x: -200,
            y: -200,
            timer: 0,
        }
    },

    watch: {
        fps() {
            window.clearInterval(this.timer)
            this.timer = window.setInterval(
                this.updateSnowflakes,
                1000 / this.fps
            )
        },
    },

    mounted() {
        const currentYear = getYear(new Date())
        const isPrankTime = isToday(new Date(currentYear, 11, 28))
        if (!isPrankTime) return
        this.init()
        this.listen("keydown", event => {
            const { altKey, key } = event
            if (!altKey) return
            switch (key.toLowerCase()) {
                case "s":
                    this.init()
                    if (this.newFlakesToAdd < 10) {
                        this.newFlakesToAdd += 3
                    }
                    break

                case "q":
                    this.stop()
                    break

                default:
                // nothing to do here
            }
        })
    },

    beforeUnmount() {
        this.stop()
    },

    methods: {
        init() {
            if (this.timer) return
            this.timer = window.setInterval(
                this.updateSnowflakes,
                1000 / this.fps
            )
            this._removeListener = this.listen(
                "mousemove",
                this.updateMouseCoordinates
            )
            this.snowColors.push(
                this.$store.state.theme.customization.primaryColor
            )
        },

        stop() {
            window.clearInterval(this.timer)
            this.timer = null
            this.snowflakes = []
            this._removeListener && this._removeListener()
        },

        createSnowflake(x, y) {
            const sizePercent = Math.random()
            const size = Math.floor(
                sizePercent * (MAX_SIZE - MIN_SIZE) + MIN_SIZE
            )
            const opacity = MIN_OPACITY + (1 - MIN_OPACITY) * Math.random()
            // Create a unique speed offset, so each flake falls at a unique rate
            const speedOffset = Math.floor(Math.random() * MAX_SPEED_OFFSET)
            // Create a unique wiggle amount based on size (bigger = more wiggle/drift)
            const wiggle =
                MIN_WIGGLE +
                MAX_WIGGLE_OFFSET * Math.random() +
                (MAX_WIGGLE - MIN_WIGGLE) * sizePercent
            const style = {
                left: `${x}px`,
                top: `${y}px`,
                zIndex: 1000000,
                width: `${size}px`,
                height: `${size}px`,
                opacity,
                borderRadius: `${size / 2}px`,
                backgroundColor:
                    this.snowColors[random(0, this.snowColors.length - 1)],
            }
            this.snowflakes.push({
                x,
                y,
                homeX: x,
                speedOffset,
                wiggle,
                sizePercent,
                style,
                wiggleCounter: Math.floor(100 * Math.random()),
            })
        },

        updateSnowflakes() {
            const windowHeight = document.documentElement.clientHeight
            for (
                let flakeIdx = this.snowflakes.length - 1;
                flakeIdx > -1;
                flakeIdx--
            ) {
                const flake = this.snowflakes[flakeIdx]
                const speed =
                    (2 + (1 - flake.sizePercent) * 5 + flake.speedOffset) *
                    SPEED_CONTROL
                const { y } = flake
                let newY = y + speed
                const wiggleCounter = (flake.wiggleCounter =
                    ++flake.wiggleCounter % 100)
                const sin = sinTable[wiggleCounter]
                const wiggle = flake.wiggle * sin
                let newX = flake.homeX + wiggle

                // If we're close to the mouse, force out of the way
                const mouseXDist = Math.abs(this.x - newX)
                const mouseYDist = Math.abs(this.y - newY)
                const influenceArea = 150
                if (mouseXDist < influenceArea && mouseYDist < influenceArea) {
                    const maxForce = 10
                    const dist = Math.sqrt(
                        mouseXDist * mouseXDist + mouseYDist * mouseYDist
                    )
                    if (dist < influenceArea) {
                        const influence = maxForce * (1 - dist / influenceArea)
                        if (this.y > newY) {
                            newY -= influence
                            if (this.x < newX) flake.homeX += influence
                            else flake.homeX -= influence
                        } else newY += influence
                    }
                }

                flake.x = newX
                flake.style.left = `${newX}px`
                flake.y = newY
                flake.style.top = `${newY}px`

                // Destroy flake if it has gone out of view by 100
                if (newY > windowHeight + 100) {
                    this.snowflakes.splice(flakeIdx, 1)
                }
            }

            // Add any new flakes
            if (++this.additionCounter % this.addNewFlakesEveryNFrames == 0) {
                this.additionCounter = 0

                const minX = -100
                const maxX = document.documentElement.clientWidth + 100
                const homeY = -100
                for (let i = 0; i < this.newFlakesToAdd; i++) {
                    const curX =
                        minX + Math.floor(Math.random() * (maxX - minX))
                    const curY = homeY + Math.floor(100 * Math.random())
                    this.createSnowflake(curX, curY)
                }
            }
        },

        updateMouseCoordinates(event) {
            const { x, y } = event
            this.x = x
            this.y = y
        },
    },
}
</script>
